import {Trailer, TrailerEntity} from "../../../model/trailer";
import {contains} from "../../../util/util";
import {TrailerContents, TrailerContentsEntity} from "../../../model/trailer-contents";
import {createParamSelector, getUseQueryResultData} from "../../../util/selector-util";
import {createSelector} from "@reduxjs/toolkit";

export type TrailerSelectorProps = {
  regionId?: string;
  companyId?: string;
  showDoNotUse?: boolean;
};

const getRegionIdParam = createParamSelector<TrailerSelectorProps | undefined, string | undefined>(params => params?.regionId);
const getCompanyIdParam = createParamSelector<TrailerSelectorProps | undefined, string | undefined>(params => params?.companyId);
const getShowDoNotUseParam = createParamSelector<TrailerSelectorProps | undefined, boolean | undefined>(params => params?.showDoNotUse);

export const trailersSelector = () => createSelector(
  [getUseQueryResultData<Trailer[]>, getRegionIdParam, getCompanyIdParam, getShowDoNotUseParam],
  (data, regionId, companyId, showDoNotUse) => {
    return selectTrailersAndTransform(
      data ?? [], {
        regionId: regionId,
        companyId: companyId,
        showDoNotUse: showDoNotUse
      }
    );
  }
);

export const trailerSelector = () => createSelector(
  [getUseQueryResultData<Trailer>],
  (data) => {
    return data ? transformTrailer(data) : undefined;
  }
);

export const transformTrailerContents = (value: TrailerContents): TrailerContentsEntity => {
  return TrailerContentsEntity.from(value);
};

export const transformTrailer = (value: Trailer): TrailerEntity => {
  return TrailerEntity.from(value);
};

export const transformTrailers = (values?: Trailer[]): TrailerEntity[] => {
  if (!values) {
    return [];
  }

  return values.map(transformTrailer);
};

export const selectTrailers = (values?: Trailer[], selector?: TrailerSelectorProps): Trailer[] => {
  if (!values) {
    return [];
  }

  return values.filter((v) => {
    let filterIn = true;

    if (selector?.regionId) {
      if (!contains(v.regionList, id => id === selector.regionId)) {
        filterIn = false;
      }
    }

    if (selector?.companyId) {
      if (selector.companyId !== v.companyId) {
        filterIn = false;
      }
    }

    const showDoNotUse = selector?.showDoNotUse ?? false;
    if (filterIn && !showDoNotUse && v.doNotUse) {
      filterIn = false;
    }

    return filterIn;
  });
};

export const selectTrailersAndTransform = (values?: Trailer[], selector?: TrailerSelectorProps): TrailerEntity[] => {
  const selected = selectTrailers(values, selector);
  return transformTrailers(selected);
};
